import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useEffect, useState } from 'react'
import IconButton from 'core/elements/button/IconButton'
import Dropdown from 'core/elements/dropdown'

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  dropdown: {
    minWidth: 150,
    '& .inputFrame': {
      borderWidth: 1,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      position: 'relative',
      zIndex: 0,
      marginTop: 0,
      height: 36,
      minHeight: 'unset',
      '&:hover': { zIndex: 1 },
    },
  },
  iconButton: {
    marginLeft: -1,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    position: 'relative',
    zIndex: 0,
    '&:hover': { zIndex: 1 },
  },
}))

export interface SortFieldOption {
  value: string
  label: string
}

export interface ServerSideSortProps {
  /** Available sort field options */
  sortOptions: SortFieldOption[]
  /** Callback when sort changes */
  onSort: (sortKey: string, sortDir: 'asc' | 'desc') => void
  /** Loading state */
  isLoading?: boolean
  /** Default sort key */
  defaultSortKey?: string
  /** Default sort direction */
  defaultSortDir?: 'asc' | 'desc'
  /** Current sort key (controlled) */
  sortKey?: string
  /** Current sort direction (controlled) */
  sortDir?: 'asc' | 'desc'
}

/**
 * Generic server-side sort component.
 *
 * Provides a dropdown for selecting sort field and a button for toggling sort direction.
 * Can be used in controlled or uncontrolled mode.
 */
export const ServerSideSort: React.FC<ServerSideSortProps> = ({
  sortOptions,
  onSort,
  isLoading = false,
  defaultSortKey,
  defaultSortDir = 'desc',
  sortKey: controlledSortKey,
  sortDir: controlledSortDir,
}) => {
  const classes = useStyles()

  // Use first option as default if not specified
  const initialSortKey = defaultSortKey || sortOptions[0]?.value || 'created_at'

  // Internal state for uncontrolled mode
  const [internalSortKey, setInternalSortKey] = useState<string>(initialSortKey)
  const [internalSortDir, setInternalSortDir] = useState<'asc' | 'desc'>(defaultSortDir)

  // Use controlled values if provided, otherwise use internal state
  const sortKey = controlledSortKey ?? internalSortKey
  const sortDir = controlledSortDir ?? internalSortDir

  // Sync internal state when controlled values change
  useEffect(() => {
    if (controlledSortKey !== undefined) {
      setInternalSortKey(controlledSortKey)
    }
  }, [controlledSortKey])

  useEffect(() => {
    if (controlledSortDir !== undefined) {
      setInternalSortDir(controlledSortDir)
    }
  }, [controlledSortDir])

  const handleSortKeyChange = useCallback(
    (value: string) => {
      setInternalSortKey(value)
      onSort(value, sortDir)
    },
    [sortDir, onSort],
  )

  const handleToggleSortDir = useCallback(() => {
    const newDir = sortDir === 'asc' ? 'desc' : 'asc'
    setInternalSortDir(newDir)
    onSort(sortKey, newDir)
  }, [sortKey, sortDir, onSort])

  return (
    <div className={classes.root}>
      <Dropdown
        className={classes.dropdown}
        items={sortOptions}
        value={sortKey}
        onChange={handleSortKeyChange}
        disabled={isLoading}
      />
      <IconButton
        className={classes.iconButton}
        onClick={handleToggleSortDir}
        disabled={isLoading}
        size="md"
        info={sortDir === 'asc' ? 'Sorted Ascending' : 'Sorted Descending'}
        icon={sortDir === 'asc' ? 'sort-amount-desc' : 'sort-amount-asc'}
      />
    </div>
  )
}

export default ServerSideSort
